import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useStore } from '../lib/useStore';
import { api, ApiError } from '../lib/api';
import { MaterialSymbol } from './icons';

/**
 * App shell in the "Conversational Concierge" design: white sidebar with
 * indigo active states on a soft-slate canvas, topbar with page title +
 * plan chip, and an upgrade CTA when the store is on the FREE plan.
 */
export default function AppFrame(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const { store, loading } = useStore();

  const isConversations = location.pathname.startsWith('/conversations');
  const isSettings = location.pathname.startsWith('/settings');
  const isAnalytics = !isConversations && !isSettings;

  const onUpgrade = async (): Promise<void> => {
    try {
      const { confirmationUrl } = await api.startBilling();
      // Top-level redirect breaks out of the embedded iframe.
      window.top
        ? (window.top.location.href = confirmationUrl)
        : (window.location.href = confirmationUrl);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to start upgrade';
      window.shopify?.toast.show(msg, { isError: true });
    }
  };

  // Initials for the footer avatar, e.g. "bibeks-market" -> "BM".
  const shopInitials = store
    ? store.shop
        .replace('.myshopify.com', '')
        .split(/[-_.]/)
        .map((part) => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('')
    : '…';

  return (
    <div className="cg-app">
      <aside className="cg-sidebar">
        <div className="cg-logo">
          <div className="cg-logo-mark">
            <MaterialSymbol name="bolt" size={20} fill />
          </div>
          <div className="cg-logo-text">
            <div className="cg-logo-name">ChatForge</div>
            <div className="cg-logo-sub">Concierge Mode</div>
          </div>
        </div>

        <nav className="cg-nav">
          <button
            type="button"
            className={`cg-nav-item${isConversations ? ' is-active' : ''}`}
            onClick={() => navigate('/conversations')}
          >
            <MaterialSymbol name="chat" size={20} />
            <span>Conversations</span>
          </button>
          {store?.plan === 'FREE' ? (
            <button
              type="button"
              className="cg-nav-item is-locked"
              title="Upgrade to PRO to unlock analytics"
              onClick={() =>
                window.shopify?.toast.show('Analytics is a PRO feature — upgrade to unlock it')
              }
            >
              <MaterialSymbol name="insights" size={20} />
              <span>Analytics</span>
              <MaterialSymbol name="lock" size={16} />
            </button>
          ) : (
            <button
              type="button"
              className={`cg-nav-item${isAnalytics ? ' is-active' : ''}`}
              onClick={() => navigate('/')}
            >
              <MaterialSymbol name="insights" size={20} fill={isAnalytics} />
              <span>Analytics</span>
            </button>
          )}
          <button
            type="button"
            className={`cg-nav-item${isSettings ? ' is-active' : ''}`}
            onClick={() => navigate('/settings')}
          >
            <MaterialSymbol name="settings" size={20} fill={isSettings} />
            <span>Settings</span>
          </button>
        </nav>

        {store?.plan === 'FREE' ? (
          <button
            type="button"
            className="cg-btn-primary cg-sidebar-upgrade"
            onClick={() => void onUpgrade()}
          >
            Upgrade to PRO
          </button>
        ) : null}

        <div className="cg-sidebar-footer">
          <div className="cg-avatar">{shopInitials}</div>
          <div className="cg-footer-text" style={{ minWidth: 0 }}>
            <div className="cg-footer-name">
              {store ? store.shop.replace('.myshopify.com', '') : loading ? 'Loading…' : 'Not connected'}
            </div>
            <div className="cg-footer-plan">{store ? `${store.plan} plan` : ''}</div>
          </div>
        </div>
      </aside>

      <div className="cg-main">
        <Outlet />
      </div>
    </div>
  );
}
